import { Carousel, Col, Row } from 'antd';
import React, { useMemo, useState } from 'react';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import { useMediaQuery } from 'react-responsive';

type Props = {
  pictureUrls: string[];
  buttons?: React.ReactNode[];
  fullWidth?: boolean;
  className?: string;
  theme: DefaultTheme;
};

const GalleryButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  right: ${(props) => props.theme.grid.getGridColumns(1, 1)};
  @media ${(props) => props.theme.breakpoints.smd} {
    right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  }
  bottom: 24px;
  z-index: 1;
`;

const MainImageContainer = styled.div<{ $fullWidth: boolean }>`
  position: relative;
  height: 100%;
  border-top-left-radius: ${(props) =>
    props.$fullWidth ? 0 : props.theme.borderRadius};
  border-bottom-left-radius: ${(props) =>
    props.$fullWidth ? 0 : props.theme.borderRadius};
  border-top-right-radius: ${(props) => props.theme.borderRadius};
  border-bottom-right-radius: ${(props) => props.theme.borderRadius};
  overflow: hidden;

  @media ${(props) => props.theme.breakpoints.smd} {
    border-top-right-radius: ${(props) =>
      props.$fullWidth ? 0 : props.theme.borderRadius};
    border-bottom-right-radius: ${(props) =>
      props.$fullWidth ? 0 : props.theme.borderRadius};
  }
`;

const MiniImageContainer = styled.div<{ $fullWidth: boolean }>`
  height: 100%;
  margin-left: 24px;
  border-top-left-radius: ${(props) => props.theme.borderRadius};
  border-bottom-left-radius: ${(props) => props.theme.borderRadius};
  border-top-right-radius: ${(props) =>
    props.$fullWidth ? 0 : props.theme.borderRadius};
  border-bottom-right-radius: ${(props) =>
    props.$fullWidth ? 0 : props.theme.borderRadius};
  overflow: hidden;
`;

const MyImage = styled.div<{ url: string }>`
  background-image: linear-gradient(
      352.5deg,
      rgba(51, 46, 49, 0.3) 0%,
      rgba(209, 198, 205, 0) 20%,
      rgba(209, 198, 205, 0) 80%,
      rgba(51, 46, 49, 0.3) 100%
    ),
    linear-gradient(
      270deg,
      rgba(51, 46, 49, 0.2) 0%,
      rgba(209, 198, 205, 0) 15%,
      rgba(209, 198, 205, 0) 85%,
      rgba(51, 46, 49, 0.2) 100%
    ),
    url(${(props) => props.url});
  opacity: 0.9;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  height: 100%;
  width: 100%;
`;

const PrevArrow = styled.div<{ $fullWidth: boolean }>`
  &:before {
    display: none;
  }
  width: ${(props) => (props.$fullWidth ? '40px' : '32px')} !important;
  height: ${(props) => (props.$fullWidth ? '40px' : '32px')} !important;
  background-image: url(/images/prev${(props) =>
      props.$fullWidth ? '' : '_black'}.svg) !important;
  left: ${(props) =>
    props.$fullWidth
      ? props.theme.grid.getGridColumns(1, 1)
      : '16px'} !important;
  z-index: 1;
`;

const NextArrow = styled.div<{ $fullWidth: boolean }>`
  &:before {
    display: none;
  }
  width: ${(props) => (props.$fullWidth ? '40px' : '32px')} !important;
  height: ${(props) => (props.$fullWidth ? '40px' : '32px')} !important;
  background-image: url(/images/next${(props) =>
      props.$fullWidth ? '' : '_black'}.svg) !important;
  right: ${(props) =>
    props.$fullWidth
      ? props.theme.grid.getGridColumns(1, 1)
      : '16px'} !important;
  z-index: 1;
`;

const SmDownHiddenCol = styled(Col)`
  @media ${(props) => props.theme.breakpoints.smd} {
    display: none;
  }
`;

const ImageCarousel: React.FC<Props> = ({
  pictureUrls,
  buttons = [],
  fullWidth = false,
  className,
  theme,
}) => {
  const isSmDown = useMediaQuery({ query: theme.breakpoints.smd });

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImageIndexes = useMemo(() => {
    const indexes: number[] = [];
    if (pictureUrls.length > 1) {
      indexes.push((currentImageIndex + 1) % pictureUrls.length);
    }
    if (pictureUrls.length > 2) {
      indexes.push((currentImageIndex + 2) % pictureUrls.length);
    }
    if (pictureUrls.length > 3) {
      indexes.push((currentImageIndex + 3) % pictureUrls.length);
    }
    return indexes;
  }, [currentImageIndex, pictureUrls.length]);

  const multiImage = useMemo(() => pictureUrls.length >= 2, [
    pictureUrls.length,
  ]);

  return (
    <div className={className}>
      <Row style={{ height: '100%' }}>
        <Col xs={24} md={multiImage ? 18 : 24} style={{ height: '100%' }}>
          <MainImageContainer as="div" $fullWidth={fullWidth}>
            <Carousel
              arrows={true}
              dots={!isSmDown}
              prevArrow={<PrevArrow $fullWidth={fullWidth} />}
              nextArrow={<NextArrow $fullWidth={fullWidth} />}
              lazyLoad={'progressive'}
              afterChange={(current) => setCurrentImageIndex(current)}
            >
              {pictureUrls.map((url) => (
                <MyImage key={url} url={url} />
              ))}
            </Carousel>

            <GalleryButtonsContainer>{buttons}</GalleryButtonsContainer>
          </MainImageContainer>
        </Col>

        <SmDownHiddenCol span={multiImage ? 6 : 0}>
          <Row gutter={[0, 24]} style={{ height: '100%' }}>
            {nextImageIndexes.map((index) => (
              <Col
                key={index}
                span={24}
                style={{ height: 'calc(33.33% - 16px)' }}
              >
                <MiniImageContainer as="div" $fullWidth={fullWidth}>
                  <MyImage url={pictureUrls[index]} />
                </MiniImageContainer>
              </Col>
            ))}
          </Row>
        </SmDownHiddenCol>
      </Row>
    </div>
  );
};

export default withTheme<typeof ImageCarousel>(styled(ImageCarousel)`
  position: relative;
  height: 100%;

  .slick-dots-bottom {
    bottom: 24px;
  }

  .ant-carousel,
  .slick-slider,
  .slick-list,
  .slick-track,
  .slick-slide,
  .slick-slide > div {
    height: 100%;
  }
`);
